import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Chip,
  useTheme,
  IconButton,
  Divider,
  Button,
} from '@mui/material';
import { Close, Visibility, OpenInNew } from '@mui/icons-material';
import { FilterContext } from '../../contexts/FilterContext';
import fetchData from '../../helpers/fetchData';
import { tokens } from '../../theme';
import AdPreviewModal from './AdPreviewModal';

interface AdDetailModalProps {
  open: boolean;
  onClose: () => void;
  adId: number;
}

interface AdSendHistory {
  send_date: string;
  newsletter_name: string;
  email_subject: string;
  esp_type: string;
  control_codes: string; // Comma-separated string of control codes
  campaign_id: string; // Campaign ID for this send
  // Aggregated performance metrics
  total_recipients: number;
  total_opens: number;
  total_clicks: number;
  total_leads: number;
  total_anfo_count: number;
  total_sum_anfo: number;
  total_revenue: number;
  // Calculated rates
  open_rate?: number;
  click_rate?: number;
  lead_rate?: number;
  etko?: number;
}

interface AdInfo {
  form: string;
  dynamic: number;
  product_customer: string;
  product_name: string;
  customer_id: string;
}

const AdStatisticsDetailModal: React.FC<AdDetailModalProps> = ({ open, onClose, adId }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { filterState } = useContext(FilterContext)!;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adInfo, setAdInfo] = useState<AdInfo | null>(null);
  const [sendHistory, setSendHistory] = useState<AdSendHistory[]>([]);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  useEffect(() => {
    if (!open || !adId || !filterState?.startDate || !filterState?.endDate) {
      return;
    }

    const loadAdDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        // Format dates to YYYY-MM-DD
        const formatDate = (date: Date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        const options = {
          start_date: formatDate(filterState.startDate!),
          end_date: formatDate(filterState.endDate!),
        };

        const response = (await fetchData(
          `/api/ad-statistics/details/${adId}`,
          options,
          false,
        )) as any;

        if (response.ad_info) {
          setAdInfo(response.ad_info);
        }
        if (response.send_history) {
          setSendHistory(response.send_history);
        }

      } catch (err: any) {
        console.error('Error loading ad details:', err);
        setError(err.message || 'Failed to load ad details');
      } finally {
        setLoading(false);
      }
    };

    loadAdDetails();
  }, [open, adId, filterState?.startDate, filterState?.endDate]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE', {
      timeZone: 'Europe/Berlin',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatNumber = (num: number | null | undefined) => {
    if (num === null || num === undefined) return '-';
    return num.toLocaleString('de-DE');
  };

  const formatRate = (rate: number | null | undefined) => {
    if (rate === null || rate === undefined) return '-';
    const numRate = typeof rate === 'string' ? parseFloat(rate) : rate;
    if (isNaN(numRate)) return '-';
    return `${numRate.toFixed(2)}%`;
  };

  const formatCurrency = (amount: number | string | null | undefined) => {
    if (amount === null || amount === undefined) return '-';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return '-';
    return `€${numAmount.toFixed(2)}`;
  };

  // Calculate summary totals
  const summaryTotals = React.useMemo(() => {
    if (sendHistory.length === 0) return null;

    const totalRecipients = sendHistory.reduce(
      (sum, send) => sum + (send.total_recipients || 0),
      0,
    );
    const totalOpens = sendHistory.reduce((sum, send) => sum + (send.total_opens || 0), 0);
    const totalClicks = sendHistory.reduce((sum, send) => sum + (send.total_clicks || 0), 0);
    const totalLeads = sendHistory.reduce((sum, send) => sum + (send.total_leads || 0), 0);
    const totalAnfoCount = sendHistory.reduce((sum, send) => sum + (send.total_anfo_count || 0), 0);
    const totalRevenue = sendHistory.reduce((sum, send) => {
      const revenue =
        typeof send.total_revenue === 'string'
          ? parseFloat(send.total_revenue)
          : send.total_revenue || 0;
      return sum + (isNaN(revenue) ? 0 : revenue);
    }, 0);

    // Calculate weighted averages
    const avgOpenRate = totalRecipients > 0 ? (totalOpens / totalRecipients) * 100 : 0;
    const avgClickRate = totalOpens > 0 ? (totalClicks / totalOpens) * 100 : 0;
    const avgLeadRate = totalOpens > 0 ? (totalLeads / totalOpens) * 100 : 0;
    const avgEtko = totalOpens > 0 ? (totalRevenue / totalOpens) * 1000 : 0;

    return {
      send_count: sendHistory.length,
      total_recipients: totalRecipients,
      total_opens: totalOpens,
      total_clicks: totalClicks,
      total_leads: totalLeads,
      total_anfo_count: totalAnfoCount,
      total_revenue: totalRevenue,
      open_rate: avgOpenRate,
      click_rate: avgClickRate,
      lead_rate: avgLeadRate,
      etko: avgEtko,
    };
  }, [sendHistory]);

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6">Anzeigen-Details - ID {adId}</Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Visibility />}
              onClick={() => setPreviewModalOpen(true)}
              sx={{ minWidth: 'auto' }}
            >
              Vorschau
            </Button>
          </Box>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box p={4}>
            <Typography color="error">Fehler: {error}</Typography>
          </Box>
        ) : (
          <Box>
            {/* Ad Information */}
            {adInfo && (
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>
                  Anzeigen-Informationen
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    backgroundColor: colors.primary[400],
                    border: `1px solid ${colors.grey[700]}`,
                  }}
                >
                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Kunde:
                      </Typography>
                      <Typography variant="body1">{adInfo.product_customer || '-'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Produkt:
                      </Typography>
                      <Typography variant="body1">{adInfo.product_name || '-'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Formular:
                      </Typography>
                      <Chip
                        label={adInfo.form || '-'}
                        size="small"
                        color={
                          adInfo.form === 'html'
                            ? 'error'
                            : adInfo.form === 'native'
                              ? 'success'
                              : adInfo.form === 'checkpoint'
                                ? 'warning'
                                : 'default'
                        }
                      />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Dynamisch:
                      </Typography>
                      <Chip
                        label={adInfo.dynamic ? 'Ja' : 'Nein'}
                        size="small"
                        color={adInfo.dynamic ? 'success' : 'default'}
                      />
                    </Box>
                  </Box>
                </Paper>
              </Box>
            )}

            {/* Summary Statistics */}
            {summaryTotals && (
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>
                  Gesamtleistung über alle Versendungen
                </Typography>
                <Box display="flex" gap={2} mb={2} flexWrap="wrap">
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      backgroundColor: colors.primary[400],
                      border: `1px solid ${colors.grey[700]}`,
                    }}
                  >
                    <Typography variant="caption" color="textSecondary">
                      Anzahl Versendungen
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {formatNumber(summaryTotals.send_count)}
                    </Typography>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      backgroundColor: colors.primary[400],
                      border: `1px solid ${colors.grey[700]}`,
                    }}
                  >
                    <Typography variant="caption" color="textSecondary">
                      Empfänger
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {formatNumber(summaryTotals.total_recipients)}
                    </Typography>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      backgroundColor: colors.primary[400],
                      border: `1px solid ${colors.grey[700]}`,
                    }}
                  >
                    <Typography variant="caption" color="textSecondary">
                      Öffnungen
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {formatNumber(summaryTotals.total_opens)}
                    </Typography>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      backgroundColor: colors.primary[400],
                      border: `1px solid ${colors.grey[700]}`,
                    }}
                  >
                    <Typography variant="caption" color="textSecondary">
                      Klicks
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {formatNumber(summaryTotals.total_clicks)}
                    </Typography>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      backgroundColor: colors.primary[400],
                      border: `1px solid ${colors.grey[700]}`,
                    }}
                  >
                    <Typography variant="caption" color="textSecondary">
                      Leads
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {formatNumber(summaryTotals.total_leads)}
                    </Typography>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      backgroundColor: colors.primary[400],
                      border: `1px solid ${colors.grey[700]}`,
                    }}
                  >
                    <Typography variant="caption" color="textSecondary">
                      ANFOs
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {formatNumber(summaryTotals.total_anfo_count)}
                    </Typography>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      backgroundColor: colors.primary[400],
                      border: `1px solid ${colors.grey[700]}`,
                    }}
                  >
                    <Typography variant="caption" color="textSecondary">
                      Öffnungsrate
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {formatRate(summaryTotals.open_rate)}
                    </Typography>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      backgroundColor: colors.primary[400],
                      border: `1px solid ${colors.grey[700]}`,
                    }}
                  >
                    <Typography variant="caption" color="textSecondary">
                      Klickrate
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {formatRate(summaryTotals.click_rate)}
                    </Typography>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      backgroundColor: colors.primary[400],
                      border: `1px solid ${colors.grey[700]}`,
                    }}
                  >
                    <Typography variant="caption" color="textSecondary">
                      Umsatz
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {formatCurrency(summaryTotals.total_revenue)}
                    </Typography>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      backgroundColor: colors.primary[400],
                      border: `1px solid ${colors.grey[700]}`,
                    }}
                  >
                    <Typography variant="caption" color="textSecondary">
                      ETKO
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {formatCurrency(summaryTotals.etko)}
                    </Typography>
                  </Paper>
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Send History Table */}
            <Typography variant="h6" gutterBottom>
              Versendungshistorie
            </Typography>
            <TableContainer component={Paper} sx={{ backgroundColor: colors.primary[400] }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Versanddatum</TableCell>
                    <TableCell>Newsletter</TableCell>
                    <TableCell>Betreff</TableCell>
                    <TableCell>Position</TableCell>
                    <TableCell>ESP</TableCell>
                    <TableCell>Vorschau</TableCell>
                    <TableCell align="right">Empfänger</TableCell>
                    <TableCell align="right">Öffnungen</TableCell>
                    <TableCell align="right">Klicks</TableCell>
                    <TableCell align="right">Leads</TableCell>
                    <TableCell align="right">ANFOs</TableCell>
                    <TableCell align="right">Öffnungsrate</TableCell>
                    <TableCell align="right">Klickrate</TableCell>
                    <TableCell align="right">Lead-Rate</TableCell>
                    <TableCell align="right">Umsatz</TableCell>
                    <TableCell align="right">ETKO</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sendHistory.map((send, index) => {
                    // Parse control codes from comma-separated string
                    const controlCodes = send.control_codes
                      ? send.control_codes.split(',').map((code) => parseInt(code.trim(), 10))
                      : [];

                    return (
                      <TableRow key={index}>
                        <TableCell>{formatDate(send.send_date)}</TableCell>
                        <TableCell>{send.newsletter_name}</TableCell>
                        <TableCell>{send.email_subject || '-'}</TableCell>
                        <TableCell>
                          <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {controlCodes.map((code) => (
                              <Chip
                                key={code}
                                label={code === 0 ? 'Top' : `Pos ${code}`}
                                size="small"
                                color={code === 0 ? 'primary' : 'default'}
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={send.esp_type}
                            size="small"
                            color={send.esp_type === 'mailchimp' ? 'primary' : 'secondary'}
                          />
                        </TableCell>
                        <TableCell>
                          {send.esp_type === 'mailchimp' && send.campaign_id ? (
                            <IconButton
                              size="small"
                              href={`https://www.finanztrends.de/adserver/preview/show_mailchimp_campaign_html.php?c_id=${send.campaign_id}&client=ft`}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ color: colors.grey[100] }}
                            >
                              <OpenInNew fontSize="small" />
                            </IconButton>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell align="right">{formatNumber(send.total_recipients)}</TableCell>
                        <TableCell align="right">{formatNumber(send.total_opens)}</TableCell>
                        <TableCell align="right">{formatNumber(send.total_clicks)}</TableCell>
                        <TableCell align="right">{formatNumber(send.total_leads)}</TableCell>
                        <TableCell align="right">{formatNumber(send.total_anfo_count)}</TableCell>
                        <TableCell align="right">{formatRate(send.open_rate)}</TableCell>
                        <TableCell align="right">{formatRate(send.click_rate)}</TableCell>
                        <TableCell align="right">{formatRate(send.lead_rate)}</TableCell>
                        <TableCell align="right">{formatCurrency(send.total_revenue)}</TableCell>
                        <TableCell align="right">{formatCurrency(send.etko)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>

      {/* Preview Modal */}
      <AdPreviewModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        materialId={adId}
        productName={adInfo?.product_name}
      />
    </Dialog>
  );
};

export default AdStatisticsDetailModal;
